import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";

export const EntryForm = ({ fields, initial, onSubmit, submitLabel = "Simpan", testid }) => {
  const [v, setV] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await onSubmit(v);
    setBusy(false);
    if (ok) setV((p) => ({ ...initial, ...Object.fromEntries(fields.filter((f) => f.keep).map((f) => [f.name, p[f.name]])) }));
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end" data-testid={testid}>
      {fields.map((f) => (
        <label key={f.name} className={`text-xs font-medium text-pink-600 space-y-1 ${f.wide ? "col-span-2" : ""}`}>
          <span>{f.label}</span>
          {f.type === "select" ? (
            <select className="field" value={v[f.name]} onChange={(e) => set(f.name, e.target.value)} data-testid={`${testid}-${f.name}`}>
              {f.options.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}
            </select>
          ) : (
            <input
              className="field"
              type={f.type || "text"}
              required={f.required}
              min={f.min}
              step={f.type === "number" ? "any" : undefined}
              placeholder={f.placeholder}
              value={v[f.name]}
              onChange={(e) => set(f.name, e.target.value)}
              data-testid={`${testid}-${f.name}`}
            />
          )}
        </label>
      ))}
      <Button type="submit" disabled={busy} className="h-11 rounded-2xl col-span-2 sm:col-span-1" data-testid={`${testid}-submit`}>
        <Plus className="w-4 h-4 mr-1" /> {submitLabel}
      </Button>
    </form>
  );
};

export const ItemList = ({ items, render, onDelete, empty = "Belum ada data", testid }) => (
  <div className="divide-y divide-pink-50" data-testid={testid}>
    {items.length === 0 && <p className="text-sm text-gray-400 py-6 text-center">{empty}</p>}
    <AnimatePresence initial={false}>
      {items.map((it) => {
        const r = render(it);
        return (
          <motion.div key={it.id} layout initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, height: 0 }} className="flex items-center gap-3 py-3" data-testid={`${testid}-item`}>
            {r.icon}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-700 truncate">{r.title}</p>
              <p className="text-xs text-gray-400 truncate">{r.subtitle}</p>
            </div>
            {r.right}
            {onDelete && (
              <button onClick={() => onDelete(it.id)} className="p-2 rounded-full text-gray-300 hover:text-rose-500 hover:bg-rose-50 transition-colors" data-testid={`${testid}-delete`} aria-label="Hapus">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </motion.div>
        );
      })}
    </AnimatePresence>
  </div>
);

export const Stat = ({ label, value, tone = "text-gray-800", testid }) => (
  <div className="card-soft !p-4">
    <p className="text-xs text-gray-400 font-medium">{label}</p>
    <p className={`font-heading text-xl sm:text-2xl font-semibold mt-1 ${tone}`} data-testid={testid}>{value}</p>
  </div>
);

export const Section = ({ title, action, children, className = "" }) => (
  <div className={`card-soft ${className}`}>
    {(title || action) && (
      <div className="flex items-center justify-between mb-4 gap-2">
        <h3 className="font-heading text-lg font-semibold text-gray-800">{title}</h3>
        {action}
      </div>
    )}
    {children}
  </div>
);
